import type { FlightLogEngine, FlightLogRecord } from '@avisoma/shared';

const FIELD_LABELS: ReadonlyArray<[keyof FlightLogRecord, string]> = [
  ['date', 'Date'],
  ['dateRaw', 'Date (as printed)'],
  ['periodFrom', 'Period From'],
  ['periodTo', 'Period To'],
  ['aircraftReg', 'Aircraft Registration'],
  ['msn', 'MSN'],
  ['operator', 'Operator'],
  ['flightNumber', 'Flight Number'],
  ['from', 'From'],
  ['to', 'To'],
  ['position', 'Engine Position'],
  ['flightHours', 'Flight Hours'],
  ['flightHoursRaw', 'Flight Hours (as printed)'],
  ['cycles', 'Cycles'],
  ['totalHoursBefore', 'Total Hours Before'],
  ['totalCyclesBefore', 'Total Cycles Before'],
  ['totalHours', 'Total Hours After'],
  ['totalCycles', 'Total Cycles After'],
  ['thrustRating', 'Thrust Rating'],
];

function engineLabel(engine: FlightLogEngine, index: number): string {
  const name = engine.position ? `Engine ${engine.position}` : `Engine ${index + 1}`;
  return engine.esn ? `${name} — ESN ${engine.esn}` : name;
}

function engineValue(engine: FlightLogEngine): string {
  const parts = [
    engine.tsnBefore != null ? `TSN before ${engine.tsnBefore}` : undefined,
    engine.csnBefore != null ? `CSN before ${engine.csnBefore}` : undefined,
    engine.tsn != null ? `TSN ${engine.tsn}` : undefined,
    engine.csn != null ? `CSN ${engine.csn}` : undefined,
  ].filter((part): part is string => part !== undefined);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/**
 * One flight-log page record (DR-5163) — the page totals of a `Tech Log Sheets` or `Ferry Flight
 * Statement` page: its scalars as a label grid, then one line per engine. Same card shell as the
 * page's Repair Report card. Renders nothing when the record carries neither.
 */
export function FlightLogRecordCard({ title, record }: { title: string; record: FlightLogRecord }) {
  const entries = FIELD_LABELS.flatMap(([key, label]) => {
    const value = record[key];
    return value == null || typeof value === 'object' ? [] : [[label, String(value)] as const];
  });
  const engines = record.engines ?? [];
  if (entries.length === 0 && engines.length === 0) return null;
  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">{title}</h2>
      </div>
      {entries.length > 0 && (
        <div className="px-4 py-4">
          <dl className="grid grid-cols-3 gap-4">
            {entries.map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {engines.length > 0 && (
        <dl className="px-4 py-4 border-t border-gray-200 dark:border-zinc-700 space-y-3">
          {engines.map((engine, index) => (
            <div key={`${engine.esn ?? ''}-${index}`}>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{engineLabel(engine, index)}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{engineValue(engine)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
